import { useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/Layout/PageShell";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      // Backend always returns 200 to avoid enumeration; toast is a safety net.
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <Seo title="Reset your password" path="/forgot-password" />
      <section className="container-spark py-16 max-w-md">
        <div className="card-spark p-10">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-password-sent">
              <CheckCircle2 className="mx-auto text-emerald-500" size={48} />
              <h1 className="text-2xl font-bold">Check your inbox</h1>
              <p className="text-muted-foreground text-sm">
                If <span className="font-medium">{email}</span> is registered,
                you&apos;ll get a password reset link within a minute or two.
                The link expires in 60 minutes.
              </p>
              <Link to="/login" className="btn-ghost inline-flex text-sm mt-2">
                Back to login
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2">
                <Mail size={22} className="text-spark-purple" />
                <h1 className="text-2xl font-bold">Reset your password</h1>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Enter your email and we&apos;ll send you a link to set a new password.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <label className="block">
                  <span className="text-xs font-medium">Email</span>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    className="input-spark mt-1"
                    data-testid="forgot-password-email"
                    placeholder="you@school.edu"
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy || !email}
                  className="btn-primary w-full"
                  data-testid="forgot-password-submit"
                >
                  {busy ? <Loader2 className="animate-spin" size={16} /> : "Send reset link"}
                </button>
              </form>
              <div className="mt-4 text-center text-xs text-muted-foreground">
                Remembered it? <Link to="/login" className="text-spark-purple">Log in</Link>
              </div>
            </>
          )}
        </div>
      </section>
    </PageShell>
  );
}
